<div class="wrapper">
  <!-- Toolbar: the RFIs tab's (rfis-list) date / filter / sort / search / Create row -->
  <div class="action-section flex flex-row-reverse my-[20px] w-full gap-[20px]">
    @if (canCreate) {
      <app-primary-button (click)="createIssue()" class="!w-[150px]" data-testid="issues-create">
        Create
      </app-primary-button>
    }
    <!-- Export menu: the punch list / claims kebab's PDF / CSV / KMZ rows, for the filtered list -->
    <div class="export-menu mt-[7px]">
      <button
        [matMenuTriggerFor]="exportMenu"
        class="export-menu-button"
        data-testid="issues-export-menu"
        aria-label="Export issues"
        type="button">
        @if (exporting) {
          <mat-spinner color="primary" diameter="20" mode="indeterminate" strokeWidth="2" />
        } @else {
          <mat-icon>more_horiz</mat-icon>
        }
      </button>
      <mat-menu #exportMenu="matMenu" xPosition="before">
        @for (option of exportOptions; track option.format) {
          <button
            (click)="exportIssues(option.format)"
            [attr.data-testid]="'issues-export-' + option.format"
            [disabled]="!!exporting"
            mat-menu-item>
            <mat-icon>{{ option.icon }}</mat-icon>
            <span>{{ option.label }}</span>
          </button>
        }
      </mat-menu>
    </div>
    <div class="issue-filters flex flex-row gap-[20px] flex-1 mt-[7px]">
      <app-project-details-page-date-filter [defaultDateValue]="defaultDateValue" />
      <app-project-details-page-multi-section-filter
        (selectionChange)="onFilterChange($event)"
        [sections]="filterSections"
        />
      <app-project-details-page-sort-dropdown
        (valueChange)="onSortChange($event)"
        [options]="sortOptions"
        [value]="sort"
        panelClass="issue-sort-panel"
        />
      <div class="search-section flex flex-1 flex-row gap-[10px] min-w-0">
        <form [formGroup]="form" class="flex-1 min-w-0">
          <div class="search w-full">
            <mat-icon svgIcon="search"></mat-icon>
            <input
              class="search-input"
              data-testid="issues-search"
              formControlName="searchText"
              matInput
              placeholder="Search"
              />
            @if (form.controls.searchText.value) {
              <mat-icon (click)="clearSearch()" svgIcon="close"></mat-icon>
            }
          </div>
        </form>
      </div>
    </div>
  </div>

  <div class="list-section">
    <div class="issue-list-table flex flex-col gap-[40px]">
      @if ((loading && !loaded) || awaitingDefaultStatus) {
        <div class="loading">
          <mat-spinner color="primary" diameter="40" mode="indeterminate" strokeWidth="4" />
        </div>
      } @else if (pageIssues.length) {
        <!-- The RFIs tab's log table (rfis-list): same markup and styles -->
        <div class="issue-items-table">
          <table [dataSource]="pageIssues" class="mat-elevation-z8 table" data-testid="issues-table" mat-table>
            <ng-container matColumnDef="title">
              <th *matHeaderCellDef (click)="onHeaderSort('title')" [class.ellipsis-header]="isSortedBy('title')" class="sortable" mat-header-cell>
                <span class="th-text">Title</span>
                @if (isSortedBy('title')) {
                  <span>
                    <mat-icon class="sort-column">
                      {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                    </mat-icon>
                  </span>
                }
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-8">{{ row?.title }}</div>
                @if (row?.description) {
                  <div class="description text-type-14">{{ row?.description }}</div>
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="site">
              <th *matHeaderCellDef (click)="onHeaderSort('site')" [class.ellipsis-header]="isSortedBy('site')" class="sortable" mat-header-cell>
                <span class="th-text">Site / Scope</span>
                @if (isSortedBy('site')) {
                  <span>
                    <mat-icon class="sort-column">
                      {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                    </mat-icon>
                  </span>
                }
              </th>
              <td *matCellDef="let row" mat-cell>
                <div [attr.title]="siteName(row)" class="site-name text-type-11">{{ siteName(row) }}</div>
              </td>
            </ng-container>
            <ng-container matColumnDef="category">
              <th *matHeaderCellDef (click)="onHeaderSort('category')" [class.ellipsis-header]="isSortedBy('category')" class="sortable" mat-header-cell>
                <span class="th-text">Category</span>
                @if (isSortedBy('category')) {
                  <span>
                    <mat-icon class="sort-column">
                      {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                    </mat-icon>
                  </span>
                }
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="category">{{ categoryLabel(row?.type) }}</div>
              </td>
            </ng-container>
            <ng-container matColumnDef="status">
              <th *matHeaderCellDef (click)="onHeaderSort('status')" [class.ellipsis-header]="isSortedBy('status')" class="sortable text-type-6 !leading-[24px]" mat-header-cell>
                <span class="th-text">Status</span>
                @if (isSortedBy('status')) {
                  <span>
                    <mat-icon class="sort-column">
                      {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                    </mat-icon>
                  </span>
                }
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="status-chip-container">
                  <div
                    [ngClass]="'tone-' + statusTone(row?.status)"
                    class="status-chip px-[14px] rounded-full"
                    data-testid="issue-status-chip"
                    >
                    {{ statusLabel(row?.status) }}
                  </div>
                </div>
              </td>
            </ng-container>
            <ng-container matColumnDef="createdAt">
              <th *matHeaderCellDef (click)="onHeaderSort('createdAt')" [class.ellipsis-header]="isSortedBy('createdAt')" class="sortable" mat-header-cell>
                <span class="th-text">Created at</span>
                @if (isSortedBy('createdAt')) {
                  <span>
                    <mat-icon class="sort-column">
                      {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                    </mat-icon>
                  </span>
                }
              </th>
              <td *matCellDef="let row" mat-cell>
                <div class="text-type-11">{{ formatInProjectTimezone(row?.created_at, 'MMM D, YYYY') }}</div>
                <div class="text-type-14">{{ formatInProjectTimezone(row?.created_at, 'h:mm A') }}</div>
                @if (row?.createdBy?.name) {
                  <div class="actor text-type-14">{{ row.createdBy.name }}</div>
                }
              </td>
            </ng-container>
            <ng-container matColumnDef="latestActivity">
              <th *matHeaderCellDef (click)="onHeaderSort('latestActivity')" [class.ellipsis-header]="isSortedBy('latestActivity')" class="sortable" mat-header-cell>
                <span class="th-text">Latest activity</span>
                @if (isSortedBy('latestActivity')) {
                  <span>
                    <mat-icon class="sort-column">
                      {{ sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                    </mat-icon>
                  </span>
                }
              </th>
              <td *matCellDef="let row" mat-cell>
                @let activity = latestActivity(row);
                <div class="action text-type-11">{{ activity.action }}</div>
                @if (activity.actor) {
                  <div class="actor text-type-14">{{ activity.actor }}</div>
                }
                <div class="created-at text-type-14">
                  {{ formatInProjectTimezone(activity.date, 'MMM D, YYYY, h:mm A') }}
                </div>
              </td>
            </ng-container>
            <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
            <tr
              data-testid="issue-row"
              (click)="openIssue(row)"
              *matRowDef="let row; columns: displayedColumns"
              [class.highlighted-row]="row.id === selectedIssueId"
              mat-row
            ></tr>
          </table>
        </div>
      } @else {
        <div class="no-data text-type-8 mt-[80px]">No data available.</div>
      }

      <app-paginator
        (pageChanged)="onPageChanged($event)"
        [itemsPerPage]="itemsPerPage"
        [maxVisiblePages]="5"
        [totalItems]="filteredIssues.length"
        class="paginator"
        />
    </div>
  </div>
</div>
